<template>
    <div>
        <!-- Keyed so a cancelled change puts the select back on the current status -->
        <v-select :key="select_key" :value="value" :items="options" item-text="label" item-value="value" dense
            hide-details append-icon="" @change="onChange">
            <template v-slot:selection="{ item }">
                <v-chip :x-small="small" :color="item.color" text-color="white" class="font-weight-600"
                    style="cursor:pointer;">
                    <v-icon v-if="item.value === booking_closed" x-small color="white" class="mr-1">mdi-lock</v-icon>
                    {{ item.label }}
                    <v-icon small color="white" class="ml-1">mdi-chevron-down</v-icon>
                </v-chip>
            </template>
            <template v-slot:item="{ item }">
                <v-chip :small="small" :color="item.color" text-color="white" class="font-weight-600 mr-2"
                    style="cursor:pointer;">
                    {{ item.label }}
                </v-chip>
            </template>
        </v-select>

        <!-- Changing to Booking Closed: what gets locked -->
        <v-dialog v-model="close_dialog" max-width="560px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon color="#8B5CF6" class="mr-2">mdi-lock-outline</v-icon>
                    Close booking?
                </v-card-title>
                <v-card-text>
                    <div class="status-dialog-label">Locked</div>
                    <ul class="mb-4">
                        <li><b>Invitees:</b> adding, editing or deleting invitees, payments, RSVPs, Stag/Hen and
                            contributions</li>
                        <li><b>Settings:</b> all fields</li>
                        <li><b>Quotes:</b> creating, editing, booking, copying and deleting</li>
                        <li><b>Suppliers:</b> start times, final rates, groupsizes, notes, booking and payment
                            statuses, deleting services</li>
                    </ul>
                    <div class="status-dialog-label">Still works</div>
                    <ul>
                        <li>All emails, event notes, Send Quote and printing the itinerary</li>
                        <li>Supplier emails, Update / Send Final Groupsizes</li>
                        <li>Itinerary, Checklist and Documents</li>
                    </ul>
                    <p class="mt-4 mb-0">To edit again, change the status back. You'll need to type CONFIRM.</p>
                </v-card-text>
                <v-card-actions class="custom-buttons">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelChange()">Cancel</v-btn>
                    <v-btn dark color="#8B5CF6" @click="confirmClose()">Close Booking</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Leaving Booking Closed: type CONFIRM to unlock -->
        <v-dialog v-model="unlock_dialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon color="#8B5CF6" class="mr-2">mdi-lock-open-variant-outline</v-icon>
                    Unlock this event?
                </v-card-title>
                <v-card-text>
                    <p>
                        This event is <b>Booking Closed</b>, so its suppliers, settings, invitees and quotes
                        are locked. Changing the status unlocks them for editing.
                    </p>
                    <v-text-field v-model="confirmation" placeholder="Please type 'CONFIRM' to change status"
                        autofocus @keyup.enter="confirmUnlock()"></v-text-field>
                </v-card-text>
                <v-card-actions class="custom-buttons">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelChange()">Cancel</v-btn>
                    <v-btn :dark="confirmation === 'CONFIRM'" color="#8B5CF6" :disabled="confirmation !== 'CONFIRM'"
                        @click="confirmUnlock()">Change Status</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { BOOKING_CLOSED, EVENT_STATUS_OPTIONS } from "../../../utils/eventStatus"

// Event status picker. Changing to Booking Closed shows what gets locked; leaving it asks for
// CONFIRM. `change` is only emitted once the new status is confirmed, so the parent just saves
// what it gets. Class and style on the tag land on the root div, so the parent's scoped :deep()
// styles still reach the select.
export default {
    props: {
        value: { type: String, default: null },
        small: { type: Boolean, default: false },
    },
    data: () => ({
        options: EVENT_STATUS_OPTIONS,
        booking_closed: BOOKING_CLOSED,
        select_key: 0,
        close_dialog: false,
        unlock_dialog: false,
        pending_status: null,
        confirmation: null,
    }),
    methods: {
        onChange(status) {
            if (status === this.value) return
            this.pending_status = status
            if (this.value === BOOKING_CLOSED) {
                this.confirmation = null
                this.unlock_dialog = true
            } else if (status === BOOKING_CLOSED) {
                this.close_dialog = true
            } else {
                this.emitChange()
            }
        },
        confirmClose() {
            this.close_dialog = false
            this.emitChange()
        },
        confirmUnlock() {
            if (this.confirmation !== 'CONFIRM') return
            this.unlock_dialog = false
            this.emitChange()
        },
        emitChange() {
            this.$emit('change', this.pending_status)
            this.pending_status = null
        },
        cancelChange() {
            this.close_dialog = false
            this.unlock_dialog = false
            this.pending_status = null
            this.select_key++
        },
    }
}
</script>

<style lang="scss" scoped>
.status-dialog-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #7C7C7C;
    margin-bottom: 4px;
}
</style>
